import React, { PureComponent } from "react"
import classNames from "classnames"
import { Query } from "react-apollo"
import gql from "graphql-tag"

import "./founder-checkin-history.scss"

const DAY_MS = 24 * 60 * 60 * 1000

export const MY_FOUNDER_CHECKINS_QUERY = gql`
  query MY_FOUNDER_CHECKINS_QUERY($first: Int, $skip: Int) {
    myFounderCheckins(first: $first, skip: $skip) {
      id
      q1
      q2
      quarterlyChoice
      motivationalStatement
      createdAt
    }
  }
`

function relativeDate(iso) {
  if (!iso) return ""
  const days = Math.max(
    0,
    Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS)
  )
  if (days === 0) return "today"
  if (days === 1) return "yesterday"
  if (days < 14) return `${days} days ago`
  if (days < 56) return `${Math.round(days / 7)} weeks ago`
  return `${Math.round(days / 30)} months ago`
}

function exactDate(iso) {
  if (!iso) return ""
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  } catch (_) {
    return ""
  }
}

function truncate(str, max) {
  if (!str) return ""
  const s = String(str).trim().replace(/\s+/g, " ")
  if (s.length <= max) return s
  return s.slice(0, max - 1).trim() + "…"
}

class FounderCheckinHistory extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      internalOpen: false,
      expandedId: null,
      autoExpandConsumed: false,
    }
  }

  isControlled() {
    return this.props.open !== undefined
  }

  isOpen() {
    return this.isControlled() ? this.props.open : this.state.internalOpen
  }

  componentDidUpdate(prevProps) {
    // When the parent flips autoExpandFirst on, we want the first item to
    // expand when the query data arrives. Reset the "consumed" flag so the
    // next data-load can apply it.
    if (
      this.props.autoExpandFirst &&
      !prevProps.autoExpandFirst &&
      this.state.autoExpandConsumed
    ) {
      this.setState({ autoExpandConsumed: false })
    }
  }

  toggle = () => {
    const next = !this.isOpen()
    if (this.props.onToggle) {
      this.props.onToggle(next)
    } else {
      this.setState({ internalOpen: next })
    }
  }

  toggleEntry = (id) => {
    this.setState((p) => ({
      expandedId: p.expandedId === id ? null : id,
      autoExpandConsumed: true,
    }))
  }

  maybeAutoExpandFirst(checkins) {
    if (
      this.props.autoExpandFirst &&
      !this.state.autoExpandConsumed &&
      checkins.length > 0 &&
      this.state.expandedId !== checkins[0].id
    ) {
      // Defer to a microtask so we don't setState during render.
      Promise.resolve().then(() =>
        this.setState({
          expandedId: checkins[0].id,
          autoExpandConsumed: true,
        }),
      )
    }
  }

  render() {
    const open = this.isOpen()
    const { expandedId } = this.state

    return (
      <section
        className={classNames("founder-checkin-history", {
          "founder-checkin-history--open": open,
        })}
      >
        <button
          type="button"
          className="founder-checkin-history__toggle"
          onClick={this.toggle}
          aria-expanded={open}
        >
          <span
            className="founder-checkin-history__chevron"
            aria-hidden
          />
          <span className="founder-checkin-history__title">
            Your check-in history
          </span>
          <span className="founder-checkin-history__meta">
            What you said, kept for you
          </span>
        </button>

        {open && (
          <div className="founder-checkin-history__body">
            <Query
              query={MY_FOUNDER_CHECKINS_QUERY}
              variables={{ first: 20, skip: 0 }}
              fetchPolicy="cache-and-network"
              ssr={false}
            >
              {({ data, loading, error }) => {
                if (loading && (!data || !data.myFounderCheckins))
                  return (
                    <p className="founder-checkin-history__loading">
                      Loading…
                    </p>
                  )
                if (error)
                  return (
                    <p className="founder-checkin-history__error">
                      Couldn't load your check-in history.
                    </p>
                  )
                const checkins =
                  (data && data.myFounderCheckins) || []
                this.maybeAutoExpandFirst(checkins)
                if (checkins.length === 0) {
                  return (
                    <p className="founder-checkin-history__empty">
                      You haven't done any check-ins yet. Each one will be
                      saved here so you can look back.
                    </p>
                  )
                }
                return (
                  <ol
                    className="founder-checkin-history__list"
                    role="list"
                  >
                    {checkins.map((c) => {
                      const isExpanded = expandedId === c.id
                      const q1Preview = truncate(c.q1, 140)
                      const isQuarterly = Boolean(c.quarterlyChoice)
                      return (
                        <li
                          key={c.id}
                          className={classNames(
                            "founder-checkin-history__item",
                            {
                              "founder-checkin-history__item--expanded":
                                isExpanded,
                              "founder-checkin-history__item--quarterly":
                                isQuarterly,
                            }
                          )}
                        >
                          <button
                            type="button"
                            className="founder-checkin-history__item-header"
                            onClick={() => this.toggleEntry(c.id)}
                            aria-expanded={isExpanded}
                          >
                            <div className="founder-checkin-history__item-meta">
                              <span
                                className="founder-checkin-history__item-date"
                                title={exactDate(c.createdAt)}
                              >
                                {relativeDate(c.createdAt)}
                              </span>
                              {isQuarterly && (
                                <span className="founder-checkin-history__quarterly-tag">
                                  Quarterly
                                </span>
                              )}
                            </div>
                            <span className="founder-checkin-history__item-preview">
                              {q1Preview ||
                                (c.motivationalStatement
                                  ? truncate(c.motivationalStatement, 140)
                                  : "(no notes)")}
                            </span>
                            <span
                              className={classNames(
                                "founder-checkin-history__item-chevron",
                                {
                                  "founder-checkin-history__item-chevron--open":
                                    isExpanded,
                                }
                              )}
                              aria-hidden
                            />
                          </button>

                          {isExpanded && (
                            <div className="founder-checkin-history__item-body">
                              {c.q1 && (
                                <div className="founder-checkin-history__field">
                                  <span className="founder-checkin-history__field-label">
                                    What you did
                                  </span>
                                  <p>{c.q1}</p>
                                </div>
                              )}
                              {c.q2 && (
                                <div className="founder-checkin-history__field">
                                  <span className="founder-checkin-history__field-label">
                                    What changed
                                  </span>
                                  <p>{c.q2}</p>
                                </div>
                              )}
                              {c.motivationalStatement && (
                                <div className="founder-checkin-history__field">
                                  <span className="founder-checkin-history__field-label">
                                    Focus that was suggested
                                  </span>
                                  <blockquote className="founder-checkin-history__quote">
                                    {c.motivationalStatement}
                                  </blockquote>
                                </div>
                              )}
                            </div>
                          )}
                        </li>
                      )
                    })}
                  </ol>
                )
              }}
            </Query>
          </div>
        )}
      </section>
    )
  }
}

export default FounderCheckinHistory
